import React from "react";

import { dollarDisplay } from "EmployerApplication/CostEstimationUtils";

import { Table, HeaderTR, TH, TD, TR, NestedTR, SpacerTR, FooterTR, FooterTD, ToggleTR, DetailTR, CollapseRowsOrganizer, CollapseBody } from "Cobalt/TableV2";
import { SizeSwitch } from "Common/Responsive";

import makeBem from "Utils/makeBem";

const bem = makeBem("CartEmployeePriceBreakdown");

const MobileRow = ({ employee, dependentTotals }) => (
  <CollapseRowsOrganizer>
    {({ isOpen, toggle }) => (
      <>
        <ToggleTR isOpen={isOpen} toggle={toggle} withBorder={isOpen}>
          <TD>{employee.name}</TD>
          <TD>{employee.age}</TD>
          <TD />
        </ToggleTR>
        <CollapseBody isOpen={isOpen}>
          <DetailTR>
            <TD>Employee Share</TD>
            <TD />
            <TD align="right">{dollarDisplay(employee.employeeCost || null)}</TD>
          </DetailTR>
          <DetailTR>
            <TD>Company Share</TD>
            <TD />
            <TD align="right">{dollarDisplay(employee.companyCost || null)}</TD>
          </DetailTR>
          <DetailTR>
            <TD>Total Cost</TD>
            <TD />
            <TD align="right">{dollarDisplay(employee.premium || null)}</TD>
          </DetailTR>
          <SpacerTR />
          {dependentTotals && employee.dependents.length > 0 && (
            <>
              <NestedTR>
                <TD>Dependent Costs</TD>
                <TD />
                <TD />
                <TD />
              </NestedTR>
              <DetailTR nested>
                <TD>Employee Share</TD>
                <TD />
                <TD align="right">{dollarDisplay(dependentTotals.employeeCost || null)}</TD>
              </DetailTR>
              <DetailTR nested>
                <TD>Company Share</TD>
                <TD />
                <TD align="right">{dollarDisplay(dependentTotals.companyCost || null)}</TD>
              </DetailTR>
              <DetailTR nested>
                <TD>Total Cost</TD>
                <TD />
                <TD align="right">{dollarDisplay(dependentTotals.premium || null)}</TD>
              </DetailTR>
              <SpacerTR />
            </>
          )}

          {employee.dependents.map((dependent) => (
            <>
              <NestedTR>
                <TD>{dependent.type}</TD>
                <TD>{dependent.age}</TD>
                <TD />
                <TD />
              </NestedTR>
              {!dependentTotals && (
                <>
                  <DetailTR nested>
                    <TD>Employee Share</TD>
                    <TD />
                    <TD align="right">{dollarDisplay(dependent.employeeCost || null)}</TD>
                  </DetailTR>
                  <DetailTR nested>
                    <TD>Company Share</TD>
                    <TD />
                    <TD align="right">{dollarDisplay(dependent.companyCost || null)}</TD>
                  </DetailTR>
                  <DetailTR nested>
                    <TD>Total Cost</TD>
                    <TD />
                    <TD align="right">{dollarDisplay(dependent.premium || null)}</TD>
                  </DetailTR>
                </>
              )}

              <SpacerTR />
            </>
          ))}
        </CollapseBody>
      </>
    )}
  </CollapseRowsOrganizer>
);

export const MobileTable = ({ employees }) => (
  <Table>
    <thead>
      <HeaderTR>
        <TH>Employee</TH>
        <TH>Age</TH>
        <TH />
        <TH />
      </HeaderTR>
    </thead>
    {employees.map((employee) => (
      <MobileRow employee={employee} dependentTotals={employee?.dependentTotals} />
    ))}
  </Table>
);

const DesktopRow = ({ employee, dependentTotals }) => (
  <>
    <TR>
      <TD>{employee.name}</TD>
      <TD>{employee.age}</TD>
      <TD align="right">{dollarDisplay(employee.employeeCost || null)}</TD>
      <TD align="right">{dollarDisplay(employee.companyCost || null)}</TD>
      <TD align="right">{dollarDisplay(employee.premium || null)}</TD>
    </TR>
    {dependentTotals && employee.dependents.length > 0 && (
      <>
        <NestedTR>
          <TD colspan={2}>Dependent Costs</TD>
          <TD align="right">{dollarDisplay(dependentTotals.employeeCost || null)}</TD>
          <TD align="right">{dollarDisplay(dependentTotals.companyCost || null)}</TD>
          <TD align="right">{dollarDisplay(dependentTotals.premium || null)}</TD>
        </NestedTR>
        <SpacerTR />
      </>
    )}

    {employee.dependents.map((dependent) => (
      <>
        <NestedTR>
          <TD>{dependent.type}</TD>
          <TD>{dependent.age}</TD>
          {!dependentTotals ? (
            <>
              <TD align="right">{dollarDisplay(dependent.employeeCost || null)}</TD>
              <TD align="right">{dollarDisplay(dependent.companyCost || null)}</TD>
              <TD align="right">{dollarDisplay(dependent.premium || null)}</TD>
            </>
          ) : (
            <TD colspan={3} />
          )}
        </NestedTR>
        <SpacerTR />
      </>
    ))}
  </>
);

const DesktopTable = ({ employees, totals }) => (
  <Table>
    <thead>
      <TR>
        <TH>Employee</TH>
        <TH>Age</TH>
        <TH align="right">Employee Share</TH>
        <TH align="right">Company Share</TH>
        <TH align="right">Total Cost</TH>
      </TR>
    </thead>
    <tbody className={bem()}>
      {employees.map((employee) => (
        <DesktopRow employee={employee} dependentTotals={employee?.dependentTotals} />
      ))}
    </tbody>
    <tfoot>
      <FooterTR>
        <FooterTD />
        <FooterTD />
        <FooterTD align="right">{dollarDisplay(totals.totalEmployeeCost.toFixed(2))}</FooterTD>
        <FooterTD align="right">{dollarDisplay(totals.totalCompanyCost.toFixed(2))}</FooterTD>
        <FooterTD align="right">{dollarDisplay(totals.totalCost.toFixed(2))}</FooterTD>
      </FooterTR>
    </tfoot>
  </Table>
);

export const CartEmployeePriceBreakdownTable = ({ employees, totals }) => (
  <SizeSwitch xs={<MobileTable employees={employees} />} sm={<DesktopTable employees={employees} totals={totals} />} />
);

export default CartEmployeePriceBreakdownTable;
